'use client'
import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import HeroNetwork from '@/components/sections/HeroNetwork'

const ease = [0.21, 0.47, 0.32, 0.98] as const

/**
 * Hero — a looping product video fills the frame; the headline sits centred
 * over it. The client clip (a droplet into a bowl, warm ivory tones) plays
 * muted on a loop. A soft scrim + centred bloom keep the dark navy headline
 * legible over the moving image. Reduced-motion users get the still poster
 * instead of the playing clip.
 */
export default function Hero() {
  const { t, isRTL } = useLanguage()
  const sectionRef = useRef<HTMLElement>(null)
  const reduce = useReducedMotion()
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  })
  const contentY = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [0, -60])
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], reduce ? [1, 1] : [1, 0])
  // Mount-entrance initial state. `false` under reduced motion → the copy is
  // simply there (no fade); `data-anim` on each element lets the print/no-JS
  // escape hatch (globals.css / layout <noscript>) beat the SSR'd opacity:0.
  const enter = (y: number) => (reduce ? false : { opacity: 0, y })

  return (
    <section
      ref={sectionRef}
      id="top"
      dir={isRTL ? 'rtl' : 'ltr'}
      className="relative flex min-h-[100svh] items-center justify-center overflow-hidden bg-surface"
    >
      {/* Ambient logistics-network background — a coded canvas (designed, not
          lit) that lives on the cream surface instead of a video clip. */}
      <HeroNetwork />
      {/* Scrim: a gentle warm veil tames the bright clip, and a centred bloom
          lifts the area behind the copy so the dark headline always reads. */}
      <div aria-hidden className="absolute inset-0 bg-surface/35" />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(62% 60% at 50% 48%, rgba(246,239,226,0.62), transparent 72%)',
        }}
      />

      {/* ---- Centred copy ---- */}
      <motion.div
        className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-5 text-center sm:px-8"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        {t.hero.eyebrow && (
          <motion.p
            data-anim
            className="text-[11px] uppercase tracking-[0.2em] text-accent font-semibold"
            initial={enter(8)}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
          >
            {t.hero.eyebrow}
          </motion.p>
        )}

        {/* The brand name is the star of the hero — the largest element on the
            page. The distribution-chain line sits directly beneath it. */}
        <motion.h1
          className="font-display-serif text-6xl sm:text-7xl md:text-8xl leading-[1.02]"
          style={{
            color: 'var(--color-blue)',
            fontVariationSettings: '"opsz" 144, "wght" 720',
          }}
          initial={reduce ? false : { y: 28 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease }}
        >
          {t.hero.h1a}
        </motion.h1>

        {t.hero.tagline && (
          <motion.p
            data-anim
            className="mt-5 max-w-2xl text-lg sm:text-xl text-ink/80 leading-relaxed"
            initial={enter(12)}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease }}
          >
            {t.hero.tagline}
          </motion.p>
        )}

        {/* Two real buttons. The primary is the only filled element so it stays
            dominant; the secondary is a frosted ghost button (translucent ivory
            + blur + hairline) so it reads as an action and stays legible over
            any frame of the moving clip — a bare text link washed out. Both get
            a visible focus ring and a subtle press scale. */}
        <motion.div
          data-anim
          className="mt-9 flex flex-wrap items-center justify-center gap-3"
          initial={enter(16)}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease }}
        >
          <a
            href="#contact"
            className="hero-cta inline-flex items-center justify-center gap-2 bg-action px-7 py-3.5 text-sm font-semibold text-surface shadow-[0_10px_28px_-12px_rgba(123,9,7,0.5)] transition duration-200 hover:bg-accent active:scale-[0.98]"
          >
            {t.hero.cta1}
            <span aria-hidden>{isRTL ? '←' : '→'}</span>
          </a>
          <a
            href="#products"
            className="hero-cta inline-flex items-center justify-center border border-ink/25 bg-surface/70 px-6 py-3.5 text-sm font-semibold text-ink backdrop-blur-sm transition duration-200 hover:border-ink/45 hover:bg-surface/90 active:scale-[0.98]"
          >
            {t.hero.cta2}
          </a>
        </motion.div>

        <motion.p
          data-anim
          className="mt-9 max-w-xl font-display-serif italic text-sm sm:text-base text-ink-muted leading-relaxed"
          style={{ fontVariationSettings: '"opsz" 24, "wght" 400' }}
          initial={enter(8)}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5, ease }}
        >
          {t.hero.signature}
        </motion.p>
      </motion.div>

      {/* On-brand keyboard focus ring (Tailwind's focus-visible:ring utilities
          don't generate in this project — same reason the other sections write
          focus styles as plain CSS). */}
      <style>{`
        .hero-cta:focus-visible {
          outline: 2px solid var(--color-blue);
          outline-offset: 3px;
        }
      `}</style>
    </section>
  )
}
